import React, { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react'
import type { TableColumn } from '@pilot-hub/ui'
import { usePersistState } from './ListenWerkzeuge'

/** Spalte mit optionalem Sortierwert; `mobile` steuert die Kartenansicht (title/badge/meta/actions/hidden). */
export interface SpalteDef<T = any> extends TableColumn<T> {
  sortWert?: (item: T) => string | number | null | undefined
}

const SEITE = 50
const KARTEN_BIS = 1100
const INTERAKTIV = 'button,a,input,select,textarea,label,summary,[role="button"],[role="menu"],[data-no-row-click]'
const BADGE_RE = /status|zustand|aktiv|priorit/i

function useKarten() {
  const q = `(max-width: ${KARTEN_BIS - 0.02}px)`
  const [an, setAn] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches)
  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia(q)
    const f = () => setAn(mq.matches)
    f(); mq.addEventListener('change', f)
    return () => mq.removeEventListener('change', f)
  }, [q])
  return an
}

function rollen(cols: SpalteDef[]) {
  const hatTitel = cols.some(c => c.mobile === 'title')
  let meta = cols.filter(c => c.mobile === 'meta').length
  return cols.map((c, i) => {
    if (c.mobile) return c.mobile
    if (c.key === 'actions' || /^aktionen$/i.test(c.header)) return 'actions'
    if (!hatTitel && i === 0) return 'title'
    if (BADGE_RE.test(c.key) || BADGE_RE.test(c.header)) return 'badge'
    if (!c.header.trim()) return 'hidden'
    meta++
    return meta <= 3 ? 'meta' : 'hidden'
  })
}

function wert<T>(c: SpalteDef<T>, item: T) {
  if (c.sortWert) return c.sortWert(item) ?? null
  if (typeof c.accessor === 'function') return null
  const v = (item as any)[c.accessor]
  return typeof v === 'number' || typeof v === 'string' ? v : v == null ? null : String(v)
}

function vergleich(a: any, b: any) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' })
}

/** Gemeinsame Listentabelle des Admin-Moduls: Sortierung per Spaltenkopf, Paginierung ab 50 Zeilen,
 *  ganze Zeile klickbar (Enter/Leertaste), unter 1100 px als Kartenzeilen. */
export function ListenTabelle<T>({ data, columns, keyAccessor, onRowClick, emptyMessage = 'Keine Daten vorhanden', persistKey }: {
  data: T[]
  columns: SpalteDef<T>[]
  keyAccessor: (item: T) => string
  onRowClick?: (item: T) => void
  emptyMessage?: string
  persistKey?: string
}) {
  const karten = useKarten()
  const [sort, setSort] = usePersistState<{ key: string; dir: 'asc' | 'desc' } | null>(persistKey ? `${persistKey}.sort` : '', null)
  const [seite, setSeite] = useState(0)

  const sortiert = useMemo(() => {
    const col = sort && columns.find(c => c.key === sort.key)
    if (!col || !sort) return data
    const f = sort.dir === 'asc' ? 1 : -1
    return [...data].sort((a, b) => f * vergleich(wert(col, a), wert(col, b)))
  }, [data, sort, columns])

  const seiten = Math.max(1, Math.ceil(sortiert.length / SEITE))
  useEffect(() => { if (seite >= seiten) setSeite(seiten - 1) }, [seite, seiten])
  useEffect(() => { setSeite(0) }, [data.length, sort?.key, sort?.dir])
  const sicht = sortiert.length > SEITE ? sortiert.slice(seite * SEITE, (seite + 1) * SEITE) : sortiert

  const zelle = (c: SpalteDef<T>, item: T): React.ReactNode =>
    c.render ? c.render((item as any)[c.accessor as string], item)
      : typeof c.accessor === 'function' ? c.accessor(item) : ((item as any)[c.accessor] ?? '-')

  const klick = (item: T) => (e: React.MouseEvent<HTMLElement>) => {
    if (!onRowClick) return
    const n = (e.target as HTMLElement).closest?.(INTERAKTIV)
    if (n && n !== e.currentTarget && e.currentTarget.contains(n)) return
    if (window.getSelection()?.toString()) return
    onRowClick(item)
  }
  const taste = (item: T) => (e: React.KeyboardEvent<HTMLElement>) => {
    if (!onRowClick || e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(item) }
  }
  const sortieren = (key: string) =>
    setSort(s => !s || s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null)

  if (data.length === 0) return <div className="py-12 text-center text-dark-text-muted">{emptyMessage}</div>

  const zeilenKlasse = onRowClick
    ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400' : ''

  let inhalt: React.ReactNode
  if (karten) {
    const rs = rollen(columns as SpalteDef[])
    const pick = (r: string) => columns.filter((_, i) => rs[i] === r)
    const titel = pick('title'), unter = pick('subtitle'), badges = pick('badge'), metas = pick('meta'), aktionen = pick('actions')
    inhalt = (
      <ul className="space-y-2" data-ui-cards="">
        {sicht.map(item => (
          <li key={keyAccessor(item)} tabIndex={onRowClick ? 0 : undefined} onClick={klick(item)} onKeyDown={taste(item)}
            className={`rounded-xl border border-dark-border bg-dark-bg-tertiary/30 px-4 py-3 hover:bg-dark-bg-tertiary/50 ${zeilenKlasse}`}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {titel.map(c => <span key={c.key} className="break-words text-base font-semibold text-dark-text">{zelle(c, item)}</span>)}
                  {badges.map(c => <span key={c.key}>{zelle(c, item)}</span>)}
                </div>
                {unter.map(c => <div key={c.key} className="mt-0.5 text-sm text-dark-text-muted">{zelle(c, item)}</div>)}
                {metas.length > 0 && (
                  <dl className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                    {metas.map(c => (
                      <div key={c.key} className="flex min-w-0 gap-1.5">
                        <dt className="text-dark-text-subtle">{c.header}:</dt>
                        <dd className="min-w-0 break-words text-dark-text">{zelle(c, item)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
              {aktionen.length > 0 && <div className="-mr-2 -mt-1 flex shrink-0 items-center">{aktionen.map(c => <div key={c.key}>{zelle(c, item)}</div>)}</div>}
            </div>
          </li>
        ))}
      </ul>
    )
  } else {
    inhalt = (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dark-border">
              {columns.map(c => {
                const kannSort = c.key !== 'actions' && (c.sortable ?? true) && !!c.header.trim() && (!!c.sortWert || typeof c.accessor !== 'function')
                const aktiv = sort?.key === c.key ? sort.dir : null
                return (
                  <th key={c.key} className="ui-th" aria-sort={aktiv ? (aktiv === 'asc' ? 'ascending' : 'descending') : undefined}>
                    {kannSort ? (
                      <button type="button" onClick={() => sortieren(c.key)} title={`Nach ${c.header} sortieren`}
                        className="inline-flex items-center gap-1 uppercase tracking-[inherit] hover:text-dark-text focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400">
                        {c.header}
                        {aktiv === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : aktiv === 'desc' ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5 opacity-0" />}
                      </button>
                    ) : c.header}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-border">
            {sicht.map(item => (
              <tr key={keyAccessor(item)} tabIndex={onRowClick ? 0 : undefined} onClick={klick(item)} onKeyDown={taste(item)}
                className={`transition-colors ${onRowClick ? 'hover:bg-dark-bg-tertiary/50 focus-visible:bg-dark-bg-tertiary/50' : ''} ${zeilenKlasse}`}
                title={onRowClick ? 'Klicken zum Anzeigen' : undefined}>
                {columns.map(c => <td key={c.key} className={`ui-td ${c.cellClassName ?? ''}`}>{zelle(c, item)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div>
      {inhalt}
      {sortiert.length > SEITE && (
        <div className="mt-3 flex items-center justify-between gap-3 text-sm text-dark-text-muted">
          <span>{seite * SEITE + 1}–{Math.min(sortiert.length, (seite + 1) * SEITE)} von {sortiert.length}</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setSeite(s => Math.max(0, s - 1))} disabled={seite === 0} aria-label="Vorherige Seite"
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-dark-border hover:bg-dark-bg-tertiary disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            <span>Seite {seite + 1} / {seiten}</span>
            <button type="button" onClick={() => setSeite(s => Math.min(seiten - 1, s + 1))} disabled={seite >= seiten - 1} aria-label="Nächste Seite"
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-dark-border hover:bg-dark-bg-tertiary disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  )
}
